import React from 'react';
import { Container } from 'react-grid-system';
import { NavBadge } from '../components/NavBadge';
import { NavLanguageMenu } from '../components/NavLanguageMenu';
import { NavUserMenu } from '../components/NavUserMenu';
import { NavCommandSearch } from '../components/NavCommandSearch';
import { NavMobileDrawer } from '../components/NavMobileDrawer';
import { NavNotifications } from '../components/NavNotifications';
import { cn } from 'utils/cn';
import { NavItemIcon, navHairlineClass, navMutedClass, navSurfaceClass } from '../navIcons';

const isItemActive = (item, activePath) => {
    if (typeof item?.isActive === 'function') {
        return item.isActive(activePath);
    }
    return activePath === item?.to;
};

const CommunityLogo = ({ community, onNavigate, logoTo = '/my_communities', pending = false }) => {
    const name = community?.name || community?.title || 'Joinee';
    const logoSrc = community?.logo || community?.logoUrl;
    const initials = name.slice(0, 2).toUpperCase();

    if (pending) {
        return <div className="h-8 w-8 animate-pulse rounded-lg bg-muted" aria-hidden="true" />;
    }

    return (
        <button
            type="button"
            onClick={() => onNavigate && onNavigate(logoTo)}
            className="flex items-center gap-2.5 shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={name}
        >
            {logoSrc ? (
                <img
                    src={logoSrc}
                    alt={name}
                    className="h-8 w-auto max-w-[9rem] object-contain"
                />
            ) : (
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">
                    {initials}
                </div>
            )}
        </button>
    );
};

export const DualTierWorkspaceNav = ({
    items = [],
    activePath = '/my_communities',
    user,
    community,
    onNavigate,
    onLogout,
    onItemClick,
    languages,
    selectedLanguage,
    onSelectLanguage,
    unreadNotifications = 3,
    notifications,
    onNotificationsOpen,
    onNotificationDismiss,
    onNotificationsClear,
    onNotificationsLoadMore,
    hasMoreNotifications = false,
    loadingMoreNotifications = false,
    showSupport,
    onSupport,
    className,
    languageSlot,
    notificationsSlot,
    userSlot,
    extraSlot,
    hideLogo = false,
    logoPending = false,
    hideSearch = false,
    hideAccountChrome = false,
    logoTo = '/my_communities',
    onSearch,
    searchScopes,
    surfaceClassName = navSurfaceClass,
}) => {
    const handleItemClick = (item) => {
        if (onItemClick) {
            onItemClick(item);
            return;
        }
        if (item?.onClick) {
            item.onClick();
            return;
        }
        if (onNavigate && item?.to) {
            onNavigate(item.to);
        }
    };

    return (
        <header
            data-app-header
            className={cn(
                'sticky top-0 z-40 w-full',
                surfaceClassName,
                'shadow-[0_1px_0_0_color-mix(in_srgb,var(--dark-secondary-color)_55%,transparent),0_12px_24px_-14px_color-mix(in_srgb,var(--dark-secondary-color)_40%,transparent)]',
                className,
            )}
        >
            <div className={cn('border-b', navHairlineClass)}>
                <Container className="!px-[15px]">
                    <div className="h-14 flex items-center justify-between gap-2 md:gap-4">
                    <div className="flex items-center gap-2 min-w-0 shrink-0">
                        {!hideLogo && (
                            <CommunityLogo community={community} onNavigate={onNavigate} logoTo={logoTo} pending={logoPending} />
                        )}
                    </div>
                    {!hideSearch && (
                        <div
                            data-testid="nav-mobile-search"
                            className="flex flex-1 justify-center md:hidden"
                        >
                            <NavCommandSearch
                                compact
                                onSearch={onSearch}
                                onNavigate={onNavigate}
                                searchScopes={searchScopes}
                            />
                        </div>
                    )}

                    {!hideSearch && (
                        <div className="hidden md:flex flex-1 max-w-md justify-center">
                            <NavCommandSearch
                                onSearch={onSearch}
                                onNavigate={onNavigate}
                                searchScopes={searchScopes}
                                className="w-full max-w-sm"
                            />
                        </div>
                    )}

                    <div className="flex items-center gap-2 shrink-0">
                        {extraSlot}

                        {languageSlot !== undefined ? languageSlot : (
                            <NavLanguageMenu
                                languages={languages}
                                selectedLanguage={selectedLanguage}
                                onSelectLanguage={onSelectLanguage}
                                compact
                            />
                        )}

                        {notificationsSlot !== undefined ? notificationsSlot : (
                            <NavNotifications
                                notifications={notifications}
                                unreadCount={unreadNotifications}
                                hasMore={hasMoreNotifications}
                                loadingMore={loadingMoreNotifications}
                                onNavigate={onNavigate}
                                onOpen={onNotificationsOpen}
                                onDismiss={onNotificationDismiss}
                                onClearAll={onNotificationsClear}
                                onLoadMore={onNotificationsLoadMore}
                            />
                        )}

                        {userSlot !== undefined ? userSlot : (user ? (
                            <NavUserMenu
                                user={user}
                                onNavigate={onNavigate}
                                onLogout={onLogout}
                                showSupport={showSupport}
                                onSupport={onSupport}
                            />
                        ) : (
                            <button
                                type="button"
                                onClick={() => onNavigate && onNavigate('/login')}
                                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground"
                            >
                                Log in
                            </button>
                        ))}

                        {items.length > 0 && (
                            <div className="md:hidden">
                                <NavMobileDrawer
                                    items={items}
                                    activePath={activePath}
                                    user={user}
                                    onNavigate={onNavigate}
                                    onItemClick={handleItemClick}
                                    onLogout={onLogout}
                                    languages={languages}
                                    selectedLanguage={selectedLanguage}
                                    onSelectLanguage={onSelectLanguage}
                                    languageSlot={languageSlot}
                                    hideAccountChrome={hideAccountChrome}
                                    surfaceClassName={surfaceClassName}
                                />
                            </div>
                        )}
                    </div>
                    </div>
                </Container>
            </div>

            {items.length > 0 && (
                <div className={cn('hidden md:block', surfaceClassName)}>
                    <Container className="!px-[15px]">
                        <nav className="hidden md:flex items-center gap-1 sm:gap-2 overflow-x-auto no-scrollbar py-0.5">
                            {items.map((item) => {
                                const isActive = isItemActive(item, activePath);
                                return (
                                    <button
                                        key={item.id || item.to}
                                        type="button"
                                        onClick={() => handleItemClick(item)}
                                        className={cn(
                                            'group relative flex items-center gap-2 px-3 py-2.5 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap cursor-pointer border-b-2',
                                            isActive
                                                ? 'text-primary font-semibold border-primary'
                                                : cn(navMutedClass, 'hover:text-[var(--primary-text-color)] border-transparent')
                                        )}
                                    >
                                        <NavItemIcon
                                            item={item}
                                            className={cn(
                                                'transition-colors',
                                                isActive ? 'text-primary' : cn(navMutedClass, 'group-hover:text-[var(--primary-text-color)]')
                                            )}
                                        />
                                        <span>{item.label}</span>
                                        {typeof item.badge === 'string' || typeof item.badge === 'number'
                                            ? (item.badge ? (
                                                <NavBadge
                                                    variant={isActive ? 'primary' : 'secondary'}
                                                    className="h-4 px-1.5 text-[10px]"
                                                >
                                                    {item.badge}
                                                </NavBadge>
                                            ) : null)
                                            : item.badge}

                                    </button>
                                );
                            })}
                        </nav>
                    </Container>
                </div>
            )}
            <div
                data-testid="app-header-rule"
                aria-hidden
                className="h-px w-full bg-[var(--dark-secondary-color)]/70"
            />
        </header>
    );
};
